/**
 * The visit to your site.
 *
 * An applicant was told a visit would happen and told by letter that it had,
 * and their own page said nothing about either. That is the one part of the
 * procedure that happens where they live, and it was the part they could not
 * look up.
 *
 * What they see is what the officer wrote, in the language they are reading,
 * with the photographs. Nothing is softened: if the visit found the site
 * different from the application, it says so, because that is the thing they
 * would most want the chance to answer.
 */

import { useCallback, useEffect, useState } from "react";
import { bi, type Bilingual, type Provenance } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import { formatDate } from "../format.js";
import { Badge, Callout, EmptyState, ErrorNote, Loading, Panel, ProvenanceRow } from "./ui.js";

interface Photo {
  id: string;
  url: string;
  caption: Bilingual;
  reported: { lat: number | null; lon: number | null; at: string | null; note: Bilingual };
}

interface Slot {
  id: string;
  startsAt: string;
  endsAt: string;
  offeredBy: "APPLICANT" | "OFFICE";
  chosen: boolean;
}

interface Rejected {
  slot: { startsAt: string; endsAt: string };
  problem: string;
  reason: Bilingual;
}

interface Row {
  id: string;
  slotsAskedAt: string | null;
  calendarUrl: string | null;
  attestedAt: string | null;
  noticeGivenAt: string | null;
  scheduledFor: string | null;
  heldAt: string | null;
  outcome: "AS_DESCRIBED" | "DIFFERS_FROM_APPLICATION" | "COULD_NOT_INSPECT" | null;
  findings: { en: string; ml: string } | null;
  invitee: Bilingual | null;
  applicantPresent: boolean | null;
  office: Bilingual | null;
  photos: Photo[];
}

interface Response {
  inspections: Row[];
  rule: Bilingual;
  provenance: Provenance | null;
}

const OUTCOME: Record<string, { label: Bilingual; tone: "calm" | "warn" }> = {
  AS_DESCRIBED: {
    label: bi(
      "Your site was found as your application describes it",
      "നിങ്ങളുടെ അപേക്ഷയിൽ പറഞ്ഞതുപോലെ തന്നെ സ്ഥലം കണ്ടു",
    ),
    tone: "calm",
  },
  DIFFERS_FROM_APPLICATION: {
    label: bi(
      "Something on the site was different from your application",
      "സ്ഥലത്തുള്ള ചിലത് നിങ്ങളുടെ അപേക്ഷയിൽ നിന്ന് വ്യത്യസ്തമായിരുന്നു",
    ),
    tone: "warn",
  },
  COULD_NOT_INSPECT: {
    label: bi("The visit could not be carried out", "സന്ദർശനം നടത്താൻ കഴിഞ്ഞില്ല"),
    tone: "warn",
  },
};

/**
 * One photograph. The bytes sit behind the same check as the rest of the file,
 * so it is fetched with the token rather than pointed at with a plain src.
 */
function Shot({ photo }: { photo: Photo }): JSX.Element {
  const { auth } = useSession();
  const { t } = useLang();
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let url: string | null = null;
    void (async () => {
      try {
        const token = auth ? await auth.token() : null;
        const res = await fetch(photo.url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!live) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      } catch {
        // A photograph that will not load is not worth an error on this page.
      }
    })();
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [auth, photo.url]);

  return (
    <figure className="shot">
      {src ? <img src={src} alt={t(photo.caption)} /> : <div className="shot-empty" />}
      <figcaption>
        {t(photo.caption)}
        {photo.reported.at ? (
          <div className="tiny muted">
            {t(
              bi(
                `The camera says ${formatDate(photo.reported.at, "en")}.`,
                `ക്യാമറ പറയുന്നത് ${formatDate(photo.reported.at, "ml")}.`,
              ),
            )}{" "}
            {t(photo.reported.note)}
          </div>
        ) : null}
      </figcaption>
    </figure>
  );
}

/**
 * Offering times that suit.
 *
 * Written as a date and a start time and a length, rather than two datetime
 * fields, because "Tuesday morning for an hour and a half" is how a person
 * thinks about it and "from this instant to that instant" is not.
 *
 * Offering nothing is a real answer and costs nothing: the office fixes a time
 * and tells you beforehand, which is what the rule requires of it anyway.
 */
function OfferTimes({
  applicationId,
  inspectionId,
  onOffered,
}: {
  applicationId: string;
  inspectionId: string;
  onOffered: () => void;
}): JSX.Element {
  const { api } = useSession();
  const { t } = useLang();
  const [rows, setRows] = useState([{ start: "", minutes: "90" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [rejected, setRejected] = useState<Rejected[]>([]);

  const set = (i: number, patch: Partial<{ start: string; minutes: string }>): void =>
    setRows((r) => r.map((row, j) => (j === i ? { ...row, ...patch } : row)));

  const send = async (): Promise<void> => {
    const slots = rows
      .filter((r) => r.start !== "")
      .map((r) => {
        const start = new Date(r.start);
        return {
          startsAt: start.toISOString(),
          endsAt: new Date(start.getTime() + Number(r.minutes) * 60_000).toISOString(),
        };
      });
    if (slots.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ rejected: Rejected[] }>(
        `/workflow/${applicationId}/visit/${inspectionId}/slots`,
        { slots },
      );
      // The ones that were not taken come back with the reason. Showing them is
      // the difference between "some of that did not work" and knowing which.
      setRejected(res.rejected ?? []);
      if ((res.rejected ?? []).length < slots.length) {
        setRows([{ start: "", minutes: "90" }]);
        onOffered();
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p>
        {t(
          bi(
            "Offer a few times that suit you. The office will take one of them and tell you which. Each one needs to be at least a day away, and no longer than about half a day.",
            "സൗകര്യമുള്ള ചില സമയങ്ങൾ നൽകുക. ഓഫീസ് അതിലൊന്ന് എടുത്ത് ഏതെന്ന് അറിയിക്കും. ഓരോന്നും ചുരുങ്ങിയത് ഒരു ദിവസം കഴിഞ്ഞായിരിക്കണം, അര ദിവസത്തിൽ കൂടുതലാകരുത്.",
          ),
        )}
      </p>

      {rows.map((row, i) => (
        <div className="slot-row" key={i}>
          <input
            type="datetime-local"
            value={row.start}
            onChange={(e) => set(i, { start: e.target.value })}
            aria-label={t(bi("When", "എപ്പോൾ"))}
          />
          <select value={row.minutes} onChange={(e) => set(i, { minutes: e.target.value })}
                  aria-label={t(bi("For how long", "എത്ര നേരം"))}>
            <option value="60">{t(bi("1 hour", "1 മണിക്കൂർ"))}</option>
            <option value="90">{t(bi("1 hour 30", "1 മണിക്കൂർ 30"))}</option>
            <option value="120">{t(bi("2 hours", "2 മണിക്കൂർ"))}</option>
            <option value="180">{t(bi("3 hours", "3 മണിക്കൂർ"))}</option>
          </select>
          {rows.length > 1 ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setRows((r) => r.filter((_, j) => j !== i))}
            >
              {t(bi("Remove", "നീക്കുക"))}
            </button>
          ) : null}
        </div>
      ))}

      {rejected.length > 0 ? (
        <Callout tone="warn" title={bi("Some of those did not work", "ചിലത് ശരിയായില്ല")}>
          <ul className="items" style={{ marginBottom: 0 }}>
            {rejected.map((r, i) => (
              <li className="item" key={i}>
                <div className="item-title">{formatDate(r.slot.startsAt, "en")}</div>
                <div className="item-sub">{t(r.reason)}</div>
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <ErrorNote error={error} />

      <div className="btn-row no-print">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setRows((r) => [...r, { start: "", minutes: "90" }])}
          disabled={rows.length >= 6}
        >
          {t(bi("Another time", "മറ്റൊരു സമയം"))}
        </button>
        <button
          type="button"
          className="btn"
          id="visit-offer-send"
          onClick={() => void send()}
          disabled={busy || rows.every((r) => r.start === "")}
        >
          {t(bi("Offer these times", "ഈ സമയങ്ങൾ നൽകുക"))}
        </button>
      </div>
    </div>
  );
}

/**
 * The appointment, taken away into whatever calendar the person actually uses.
 *
 * The file sits behind the same check as the rest of the application, so it is
 * fetched with the token and handed to the browser as a download rather than
 * linked to.
 */
function AddToCalendar({ url }: { url: string }): JSX.Element {
  const { auth } = useSession();
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);

  const get = async (): Promise<void> => {
    setBusy(true);
    try {
      const token = auth ? await auth.token() : null;
      const res = await fetch(`${url}?lang=${lang}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "site-inspection.ics";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="btn-row no-print">
      <button type="button" className="btn btn-secondary" onClick={() => void get()} disabled={busy}>
        {t(bi("Add to my calendar", "എന്റെ കലണ്ടറിൽ ചേർക്കുക"))}
      </button>
    </div>
  );
}

export function Inspection({ applicationId }: { applicationId: string }): JSX.Element | null {
  const { api } = useSession();
  const { t, lang } = useLang();
  const [data, setData] = useState<Response | null>(null);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<Response>(`/workflow/${applicationId}/inspection`);
      setData(res);
      // The windows already offered, for the one still being arranged.
      const arranging = res.inspections.find((i) => i.heldAt === null && i.noticeGivenAt === null);
      setSlots(
        arranging
          ? (await api.get<{ slots: Slot[] }>(
              `/workflow/${applicationId}/visit/${arranging.id}/slots`,
            )).slots
          : null,
      );
    } catch {
      setFailed(true);
    }
  }, [api, applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A file whose workflow has not started answers with nothing rather than an
  // error, and this panel simply does not appear.
  if (failed) return null;

  const rows = data?.inspections ?? [];

  return (
    <Panel
      title={bi("The visit to your site", "നിങ്ങളുടെ സ്ഥലത്തേക്കുള്ള സന്ദർശനം")}
      provenance={data?.provenance ?? null}
    >
      {data === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState
          title={bi("No visit has been arranged yet", "സന്ദർശനം ഇതുവരെ നിശ്ചയിച്ചിട്ടില്ല")}
          body={bi(
            "Before a decision is taken, somebody from the Board comes to see the site. You will be told the date beforehand, and you or somebody for you may be there.",
            "തീരുമാനത്തിന് മുൻപ് ബോർഡിൽ നിന്ന് ഒരാൾ സ്ഥലം കാണാൻ വരും. തീയതി നിങ്ങളെ മുൻകൂട്ടി അറിയിക്കും; നിങ്ങൾക്കോ നിങ്ങൾക്കുവേണ്ടി ഒരാൾക്കോ അവിടെ ഉണ്ടാകാം.",
          )}
        />
      ) : (
        <ul className="items">
          {rows.map((r) => (
            <li className="item" key={r.id}>
              {r.heldAt === null && r.noticeGivenAt === null ? (
                // Being arranged. The office has asked when suits, and nothing
                // is fixed until they take one of the times offered.
                <>
                  {/* Named, so the film can point at it in either language.
                      Anchoring by its words meant writing Malayalam out by hand
                      in the shoot script, and a chillu typed as a plain letter
                      plus a virama looks identical and matches nothing. */}
                  <div className="item-title" id="visit-asked">
                    {t(bi("When would suit you?", "നിങ്ങൾക്ക് എപ്പോഴാണ് സൗകര്യം?"))}
                  </div>
                  {(slots ?? []).length > 0 ? (
                    <div className="item-sub">
                      {t(bi("You have offered", "നിങ്ങൾ നൽകിയത്"))}:{" "}
                      {(slots ?? [])
                        .map((sl) => formatDate(sl.startsAt, lang))
                        .join("; ")}
                      . {t(bi("The office will take one and tell you.", "ഓഫീസ് ഒന്ന് എടുത്ത് അറിയിക്കും."))}
                    </div>
                  ) : null}
                  <OfferTimes
                    applicationId={applicationId}
                    inspectionId={r.id}
                    onOffered={() => void load()}
                  />
                </>
              ) : r.heldAt === null ? (
                <>
                  <div className="item-title">
                    {t(
                      bi(
                        `Somebody is coming on ${formatDate(r.scheduledFor, "en")}`,
                        `${formatDate(r.scheduledFor, "ml")}-ന് ഒരാൾ വരുന്നു`,
                      ),
                    )}
                  </div>
                  <div className="item-sub">
                    {t(
                      bi(
                        "You may be there yourself, or send somebody for you. Nothing is required of you on the day.",
                        "നിങ്ങൾക്ക് നേരിട്ട് ഉണ്ടാകാം, അല്ലെങ്കിൽ നിങ്ങൾക്കുവേണ്ടി ഒരാളെ അയക്കാം. അന്നത്തെ ദിവസം നിങ്ങളിൽ നിന്ന് ഒന്നും ആവശ്യമില്ല.",
                      ),
                    )}
                  </div>
                  <div className="item-sub">
                    {t(
                      bi(
                        "When the officer arrives we will send you a short code. Read it out to them. That is how the record shows they were here. You do not have to, and the visit goes ahead either way.",
                        "ഉദ്യോഗസ്ഥൻ എത്തുമ്പോൾ ഞങ്ങൾ ഒരു ചെറിയ കോഡ് അയക്കും. അത് അവർക്ക് വായിച്ചുകൊടുക്കുക. അവർ ഇവിടെ ഉണ്ടായിരുന്നെന്ന് രേഖയിൽ വരുന്നത് അങ്ങനെയാണ്. നിർബന്ധമില്ല; ഏതായാലും പരിശോധന നടക്കും.",
                      ),
                    )}
                  </div>
                  {r.invitee ? (
                    <div className="item-sub">
                      {t(bi("The Board has also asked", "ബോർഡ് ക്ഷണിച്ചിട്ടുള്ളത്"))}: {t(r.invitee)}
                    </div>
                  ) : null}
                  {r.calendarUrl ? <AddToCalendar url={r.calendarUrl} /> : null}
                </>
              ) : (
                <>
                  <div className="item-title">
                    {t(
                      bi(
                        `Someone came on ${formatDate(r.heldAt, "en")}`,
                        `${formatDate(r.heldAt, "ml")}-ന് ഒരാൾ വന്നു`,
                      ),
                    )}
                  </div>
                  {r.outcome && OUTCOME[r.outcome] ? (
                    <div className="item-sub">
                      <Badge text={OUTCOME[r.outcome]!.label} tone={OUTCOME[r.outcome]!.tone} />
                    </div>
                  ) : null}
                  {r.findings ? (
                    <p className="item-sub">{t(bi(r.findings.en, r.findings.ml))}</p>
                  ) : null}
                  {r.office ? (
                    <div className="item-sub muted">
                      {t(bi("Recorded by", "രേഖപ്പെടുത്തിയത്"))}: {t(r.office)}
                    </div>
                  ) : null}
                  {r.attestedAt ? (
                    <div className="item-sub muted">
                      {t(
                        bi(
                          "You confirmed the officer's arrival with the code we sent you.",
                          "ഞങ്ങൾ അയച്ച കോഡ് ഉപയോഗിച്ച് ഉദ്യോഗസ്ഥന്റെ വരവ് നിങ്ങൾ സ്ഥിരീകരിച്ചു.",
                        ),
                      )}
                    </div>
                  ) : null}
                  {r.applicantPresent === false ? (
                    <div className="item-sub muted">
                      {t(
                        bi(
                          "Nobody was there from your side. That does not count against you.",
                          "നിങ്ങളുടെ ഭാഗത്തുനിന്ന് ആരും ഉണ്ടായിരുന്നില്ല. അത് നിങ്ങൾക്ക് എതിരായി കണക്കാക്കില്ല.",
                        ),
                      )}
                    </div>
                  ) : null}
                  {r.photos.length > 0 ? (
                    <div className="shot-strip">
                      {r.photos.map((p) => (
                        <Shot key={p.id} photo={p} />
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {data?.rule ? <ProvenanceRow items={[data.provenance ?? null]} /> : null}
    </Panel>
  );
}
